'use client'

/**
 * HOME HONEST_REVIEWS PL widget — widget-of-widget container.
 *
 * v2 nested pattern (see the home IRL pair / FEATURED_PRODUCTS_SHOWCASE): each review arrives as a
 * nested SINGLE_REVIEW_CARD widget under `Widget.widgets`; the page renderer pre-renders them and
 * hands them in via `renderedChildren`; this container decides how to arrange them. Like the
 * non-PL `HonestReviewsV2` router it branches on `shouldUseDesignSystem()`: the DS chrome reuses
 * `honest-reviews-ds`'s exported classes (band / 1504px inner / heading test id / 6-up grid on
 * desktop, carousel on mobile), the legacy chrome mirrors `HonestReviewsV2Legacy` (same scss
 * module, heading + desktop grid / mobile Swiper, dynamic style classes). List-level concerns are
 * shared by both branches — the 6-review cap, visibility + batched product-view analytics — and
 * the per-card slice of section state (position, tracker, section type/id, tracking metadata)
 * reaches each child via `ReviewCardContext`.
 */

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { Carousel, CarouselContent, CarouselItem } from '@lastbrand/web-ui/carousel'
import cx from 'classnames'
import { useCallback, useMemo } from 'react'

import { RichTextDS } from '@src/components/blocks/rich-text-ds'
import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import {
  CAROUSEL_OPTS,
  carouselColumnClass,
  COLUMNS,
  containerClass,
  gridClass,
  innerClass,
  plainTextFromDocument,
  titleClass,
} from '@src/components/widgets/honest-reviews-ds/honest-reviews-ds'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { RichText as RichTextView } from '@src/quince-components/src'
import Swiper from '@src/quince-components/src/atoms/swiper'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'
import RichTextDomain from '@src/quince-site-common/src/domains/RichText'
import { useBatchedAnalyticsForProductCard } from '@src/quince-site-common/src/hooks/useBatchedAnalyticsForProductCard'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useDynamicStyle from '@src/quince-site-common/src/hooks/useDynamicStyle'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import styles from '@src/SectionRenderer/sections/honestReviewsV2/honestReviewsV2.module.scss'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { buildWidgetTrackingMetaData, toRichText } from '../widget-utils'

import { ReviewCardContextProvider } from './review-card-context'

import type { HonestReviewsWidgetData, ReviewCardData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

// Both non-PL forks cap at 6 (legacy VISIBLE_REVIEWS_LIMIT, DS COLUMNS); tie the cap to the DS
// grid's column count so the two can't drift apart.
const VISIBLE_REVIEWS_LIMIT = COLUMNS

// Section tracker — the canonical home tracker string the non-PL SectionRenderer passes to every
// home section. Keeps PL click labels (`home_page__click_product_carousel`) and the impression
// batch (`home_page_honest_reviews`) identical to the non-PL forks; do not change without auditing
// the downstream Mixpanel/GA filter chains (see ai-guidelines/project/templates/home.md).
// @i18n-ignore
const TRACKER = 'home_page'

// Widget.widgets is a recursive proto type TS can't resolve through Omit<Widget,'data'>;
// read nested SINGLE_REVIEW_CARD data via a narrow cast (same pattern as the home IRL widget).
interface ReviewNestedWidget {
  data?: { reviewCard?: ReviewCardData }
}

function HonestReviewsWidgetComponent(props: PLTypedWidgetProps<HonestReviewsWidgetData>) {
  const data = props.data.honestReviewsData

  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

  const nestedWidgets = (props as unknown as { widgets?: ReviewNestedWidget[] }).widgets
  const renderedChildren = props.renderedChildren

  const trackingMetaData = useMemo(() => buildWidgetTrackingMetaData(data?.analytics) ?? {}, [data])
  // The Contentful section type behind this widget is HONEST_REVIEWS_V2 (the proto pins it) — keep
  // it so `context.sectionType` in card click/wishlist events matches the non-PL flow exactly.
  const sectionType = CUSTOM_SECTION_TYPE.HONEST_REVIEWS_V2
  const sectionId = data?.id ?? ''

  const { styleObject, className } = useDynamicStyle({
    dynamicValueClassesMobileOverride: data?.mobileDynamicClassesOverrides,
    baseDynamicValueClasses: data?.baseDynamicClasses,
    mobileClassNamesOverride: data?.classNamesMobileOverrides,
    baseClassNames: data?.classNames,
    postFix: styles.dynamicClassesPostFix ?? '',
    wrapperClassName: styles.dynamic_class_wrapper,
  })

  const getIsVisibleTrackParams = useCallback(() => ({ ...trackingMetaData }), [trackingMetaData])
  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  // Same section-scoped impression batch as the non-PL forks (`${tracker}_honest_reviews`).
  // @i18n-ignore
  useBatchedAnalyticsForProductCard({ productViewTracker: `${TRACKER}_honest_reviews`, trackingMetaData })

  // Visible cards: cap FIRST (matching the non-PL forks' `reviews.slice(0, 6)`), then pair each
  // nested widget's data with its pre-rendered node by original index — a nested widget without an
  // enriched product contributes no slide (the non-PL forks skip productless reviews the same
  // way). Each node is wrapped in a provider bound to the card's pre-filter position, so
  // `productPosition` matches the non-PL `products.map(...)` index exactly.
  const slideEntries = useMemo(
    () =>
      (Array.isArray(nestedWidgets) ? nestedWidgets : []).slice(0, VISIBLE_REVIEWS_LIMIT).flatMap((widget, index) => {
        const reviewCard = widget.data?.reviewCard
        const node = renderedChildren?.[index]?.node
        if (!reviewCard?.product || !node) return []
        return [
          {
            key: reviewCard.id || String(index),
            node: (
              <ReviewCardContextProvider
                key={reviewCard.id || index}
                value={{ index, tracker: TRACKER, sectionType, sectionId, trackingMetaData }}
              >
                {node}
              </ReviewCardContextProvider>
            ),
          },
        ]
      }),
    [nestedWidgets, renderedChildren, sectionType, sectionId, trackingMetaData],
  )

  if (!data) return null
  if (!slideEntries.length) return null

  const headingRichTextProps = RichTextDomain.makeRichTextProps(toRichText(data.heading))

  // DS branch — mirrors `HonestReviewsDs` (same exported classes, heading test id, desktop 6-up
  // grid / mobile carousel), arranging pre-rendered children instead of mapping data to cards.
  if (shouldUseDesignSystem()) {
    return (
      <div
        ref={trackIsVisibleRef}
        id={sectionId.toLowerCase()}
        className={containerClass}
        data-testid="honest-reviews-ds"
      >
        <div className={innerClass}>
          {headingRichTextProps?.content && (
            <div data-testid={TEST_LOCATORS.HONEST_REVIEWS.HEADER(plainTextFromDocument(headingRichTextProps.content))}>
              <RichTextDS content={headingRichTextProps.content} className={titleClass} />
            </div>
          )}
          {isDesktop ? (
            <div className={gridClass}>
              {slideEntries.map((entry, idx) => (
                <div key={entry.key} data-testid={TEST_LOCATORS.HONEST_REVIEWS.PRODUCT(idx)}>
                  {entry.node}
                </div>
              ))}
            </div>
          ) : (
            <Carousel opts={CAROUSEL_OPTS}>
              <div className={carouselColumnClass}>
                <CarouselContent>
                  {slideEntries.map((entry, idx) => (
                    <CarouselItem key={entry.key} data-testid={TEST_LOCATORS.HONEST_REVIEWS.PRODUCT(idx)}>
                      {entry.node}
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </div>
            </Carousel>
          )}
        </div>
      </div>
    )
  }

  // Legacy branch — mirrors `HonestReviewsV2Legacy`'s chrome.
  const slides = slideEntries.map(entry => entry.node)

  return (
    <div className={cx(styles.container, className)} style={styleObject} ref={trackIsVisibleRef}>
      {headingRichTextProps && (
        <RichTextView {...headingRichTextProps} className={cx(headingRichTextProps.className, styles.title)} />
      )}
      {isDesktop ? (
        <div className={styles.productList}>{slides}</div>
      ) : (
        <Swiper
          className={styles.productList}
          numberOfVisibleSlides={1}
          showNavigator={false}
          showDots={false}
          variableWidth
          spaceBetween={20}
          trackingMetaData={trackingMetaData}
        >
          {slides}
        </Swiper>
      )}
    </div>
  )
}

export const HonestReviewsWidget: PLTypedWidgetInterface<HonestReviewsWidgetData, WidgetType.HONEST_REVIEWS> = {
  id: WidgetType.HONEST_REVIEWS,
  errorBoundary: PLBaseErrorBoundary,
  render: HonestReviewsWidgetComponent,
}
